/* eslint-disable max-lines */
import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { article as articleData, section } from "shared/data";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { Share } from "scmp-app/components/article/article-2.0-action-widget/share";
import type { Props as ArticleLeadingProps } from "scmp-app/components/article/article-leading";
import { ImageSize } from "scmp-app/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { GlobalChecker } from "scmp-app/components/article/article-render/global-checker";
import {
  checkIsAnnouncementArticle,
  getArticleType,
} from "scmp-app/components/article/article-render/helpers";
import {
  useArticleBaseAdvertisingInfo,
  useMobileInlineAdSlotIsDisabledFlag,
  useReduceWhitespaceByAvailableSpace,
} from "scmp-app/components/article/article-render/hooks";
import { checkArticleIsInLiteUpgradeWall } from "scmp-app/components/article/article-render/hooks/piano-integration/helpers";
import { useContentHelper } from "scmp-app/components/content/hooks";
import type { InlineAdSlotRenderProps } from "scmp-app/components/schema-render/common/inline-ad-slot/hooks";
import { SCMPLabLogo } from "scmp-app/components/scmp-lab-logo";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import type { genericArticleContent$key } from "scmp-app/queries/__generated__/genericArticleContent.graphql";

import {
  ActionWidgets,
  ActionWidgetsBar,
  Author,
  AuthorDetail,
  AuthorShortBio,
  BottomCarousel,
  Columnist,
  ColumnistWrapper,
  Content,
  ContentContainer,
  Correction,
  DesktopHeaderAdSlot,
  GenericArticleContainer,
  HeaderContainer,
  Headline,
  Leading,
  PaywallAdContainer,
  PaywallContainer,
  PublishDateTime,
  RecirculationWidgetInlineContainer,
  RecirculationWidgetRightSectionContainer,
  RelatedTopic,
  RightAvailableSpaceTracker,
  RightSectionContainer,
  RightSectionPlaceholder,
  SeriesCarousel,
  SeriesInline,
  Sponsor,
  StyledArticleContentRightSection,
  StyledArticleGoogleNewsButton,
  SubHeadline,
  TopicAndSection,
} from "./styles";

export type ArticleVariant = "generic" | "opinion" | "post-magazine" | "this-week-in-asia";

export type Props = ArticleRenderChildProps & {
  className?: string;
  extendedHeaderComponents?: React.ReactNode;
  leadingProps?: Partial<ArticleLeadingProps>;
  reference: genericArticleContent$key;
};

export const GenericArticle: FunctionComponent<Props> = ({
  className,
  extendedHeaderComponents,
  isInlinePaywallDomChanged,
  isShowInlinePaywall,
  leadingProps,
  provider,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleContent on Content {
        entityId
        flag
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
        ...articleBaseAdvertisingInfoArticle
        ...globalCheckerContent
        ...helpersCheckAnnouncementArticle
        ...helpersGetArticleTypeArticle
        ...helpersIsLiteWallArticle
        ...hooksContentHelperContent
        ...shareArticle20Content
      }
    `,
    reference,
  );

  const { zone } = useArticleBaseAdvertisingInfo(article);
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), true);
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isSubscriber = useMemo(
    () => asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false,
    [asyncRosettaState?.result?.user?.isSCMPSubscriber],
  );

  const withSticky = useMemo(() => {
    if (asyncRosettaState?.status === "success") {
      return (
        asyncRosettaState.result?.user === undefined ||
        asyncRosettaState.result?.user.isSCMPSubscriber === false
      );
    }
    return false;
  }, [asyncRosettaState]);

  const { isRecapArticleType } = useContentHelper(article);
  const articleType = getArticleType(article);

  const hasScmpLiteAccessOnly = useMemo(
    () =>
      (asyncRosettaState?.result?.hasScmpLiteAccessRight ?? false) &&
      !(asyncRosettaState?.result?.hasScmpDigitalAccessRight ?? false) &&
      !(asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false),
    [
      asyncRosettaState?.result?.hasScmpLiteAccessRight,
      asyncRosettaState?.result?.hasScmpDigitalAccessRight,
      asyncRosettaState?.result?.hasScmpPlusAccessRight,
    ],
  );

  const appSettings = asyncRosettaState?.result?.appSettings ?? {};
  const { litePaywallTimeFrame = 3 } = appSettings;

  const isLiteUpgradePaywallActive = useMemo(
    () => checkArticleIsInLiteUpgradeWall(article, hasScmpLiteAccessOnly, litePaywallTimeFrame),
    [article, hasScmpLiteAccessOnly, litePaywallTimeFrame],
  );

  const isVideoArticle = useMemo(() => article.flag === articleData.flag.video, [article.flag]);

  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    firstAdSlotSize: 1000,
    isEnabled: isShowInlinePaywall !== undefined || notEmpty(isInlinePaywallDomChanged),
    specs: {
      adSlotSize: 600,
      whitespaceAllowance: 100,
      widgets: [
        {
          maxHeight: 470,
          name: "recirculationWidget",
        },
        {
          maxHeight: 560,
          name: "topicWidget",
          withAdSlot: !isSubscriber,
        },
        {
          maxHeight: isSubscriber ? 380 : 700,
          name: "beforeYouGoWidget",
          withAdSlot: false,
        },
      ],
    },
    trackers: {
      right: RightAvailableSpaceTracker as FunctionComponent,
    },
  });

  const firstSectionEntityIds = useMemo(() => {
    const firstSection = first(article?.sectionsV2)?.value;
    return firstSection?.map(section => section?.entityId ?? "") ?? [];
  }, [article.sectionsV2]);

  const articleVariant = useMemo<ArticleVariant>(() => {
    if (firstSectionEntityIds.includes(section.postMagazine.entityId)) {
      return "post-magazine";
    }
    if (firstSectionEntityIds.includes(section.thisWeekInAsia.entityId)) {
      return "this-week-in-asia";
    }

    if (
      articleType === articleData.types.column.entityId ||
      articleType === articleData.types.opinion.entityId
    ) {
      return "opinion";
    }

    return "generic";
  }, [firstSectionEntityIds, articleType]);

  const isAnnouncementArticle = checkIsAnnouncementArticle(article);

  const inlineAdSlots = useMemo<InlineAdSlotRenderProps>(() => {
    if (isDesktopUp) {
      return isSubscriber && !isLiteUpgradePaywallActive
        ? {
            d7: {
              adUnit: "outstream1",
              isDisabled: isAnnouncementArticle || undefined,
            },
          }
        : {
            d1: {
              adUnit: "outstream1",
              isDisabled: isAnnouncementArticle || undefined,
            },
            d7: {
              adUnit: "d_native5",
              isDisabled: isAnnouncementArticle || undefined,
            },
          };
    }

    if (isShowInlinePaywall !== false) {
      return {
        "0": {
          adUnit: "outstream1",
          isDisabled: isAnnouncementArticle || undefined,
        },
      };
    }

    return {
      "0": {
        adUnit: "m_banner3",
        autoRefreshOptions: {
          scrollBackRefreshOptions: {
            slotArticleEntityId: article.entityId,
          },
        },
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isAnnouncementArticle || undefined,
        sizes: [
          [320, 100],
          [320, 50],
          [300, 100],
          [300, 50],
        ],
      },
      "1": {
        adUnit: "outstream1",
        isDisabled: isAnnouncementArticle || undefined,
      },
      "2": {
        adUnit: "m_banner1",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isAnnouncementArticle || undefined,
        sizes: [[300, 250]],
      },
      "3": {
        adUnit: "m_lrec1",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
        sizes: [
          [300, 250],
          [320, 480],
        ],
      },
      "4": {
        adUnit: "m_lrec2",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
        sizes: [[300, 250]],
      },
      "5": {
        adUnit: "m_lrec4d",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
        sizes: [
          [300, 250],
          [320, 480],
        ],
      },
      "6": {
        adUnit: "m_lrec4e",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
        sizes: [[300, 250]],
      },
      "7": {
        adUnit: "m_lrec4f",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
        sizes: [[300, 250]],
      },
      "8": {
        adUnit: "m_lrec4f",
        breakpoint: theme.breakpoints.down("desktop"),
        isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
        sizes: [[300, 250]],
      },
      "9": {
        adUnit: "m_native5",
        isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
      },
    };
  }, [
    article.entityId,
    isAnnouncementArticle,
    isDesktopUp,
    isInlineAdSlotDisabled,
    isLiteUpgradePaywallActive,
    isShowInlinePaywall,
    isSubscriber,
  ]);

  return (
    <GenericArticleContainer $articleVariant={articleVariant} className={className}>
      <DesktopHeaderAdSlot withSticky={withSticky}>
        {provider.adslot({
          adUnit: "d_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [970, 250],
            [728, 90],
            [970, 90],
          ],
        })}
      </DesktopHeaderAdSlot>
      <HeaderContainer>
        {extendedHeaderComponents}
        <BaseLinkContextProvider
          customQueryParameters={{
            module: "breadcrumb",
            pgtype: "article",
          }}
        >
          <TopicAndSection>
            {provider.topic()}
            {provider.sections()}
          </TopicAndSection>
        </BaseLinkContextProvider>
        {articleVariant === "opinion" && (
          <Columnist>
            {provider.authorAvatars({
              isColored: true,
              variant: "opinion",
              withoutLink: true,
            })}
            <ColumnistWrapper>
              {provider.headlineTag({ variant: "generic" })}
              {provider.authorNames({ withLocation: false })}
            </ColumnistWrapper>
          </Columnist>
        )}
        <Headline $articleVariant={articleVariant}>
          {provider.headline({ hideTag: articleVariant === "opinion", variant: "generic" })}
        </Headline>
        <SubHeadline>{provider.subHeadline()}</SubHeadline>
        <Sponsor>
          {provider.sponsor({
            sponsorTagStyle: {
              layoutDirection: "horizontal",
            },
          })}
        </Sponsor>
        <ActionWidgets>
          <ActionWidgetsBar>
            {!isVideoArticle && provider.readingTime()}
            <Share position="article_header" reference={article} />
            {provider.actionWidget({ variant: "article-2.0" })}
            {provider.speechPlayer({
              variant: "article-2.0-minify",
            })}
            {provider.speechPlayerPortal({
              variant: "article-2.0-minify",
              withMinifyCleanUpButton: false,
            })}
          </ActionWidgetsBar>
          <StyledArticleGoogleNewsButton articleId={article.entityId} />
        </ActionWidgets>
      </HeaderContainer>
      {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}
      <StyledArticleContentRightSection
        {...reduceWhitespaceByAvailableSpaceSpec.right}
        entityId={article.entityId}
        provider={provider}
        shouldDisableAds={isAnnouncementArticle}
      >
        {shouldRenderWidgets => (
          <>
            {shouldRenderWidgets["recirculationWidget"] && (
              <RecirculationWidgetRightSectionContainer $stickyStackedHeight={appBarStickyHeight}>
                {provider.recirculationWidget({
                  inlineAds: provider.adslot({
                    adUnit: "d_native2b",
                    breakpoint: theme.breakpoints.up("desktop"),
                    isDisabled: isAnnouncementArticle,
                    sizes: ["fluid"],
                  }),
                  variant: "right-hand-side",
                })}
                {provider.adslot({
                  adUnit: "d_lrec2",
                  breakpoint: theme.breakpoints.up("desktop"),
                  isDisabled: isAnnouncementArticle,
                  sizes: [
                    [300, 250],
                    [300, 600],
                  ],
                  stickyLabel: true,
                  stickySpacing: 40,
                  withLabel: true,
                  withLabelBackground: true,
                  withSticky: true,
                })}
              </RecirculationWidgetRightSectionContainer>
            )}

            {shouldRenderWidgets["topicWidget"] && (
              <RightSectionContainer $stickyStackedHeight={appBarStickyHeight}>
                {provider.topicWidget({
                  inlineAds: provider.adslot({
                    adUnit: "d_native2d",
                    breakpoint: theme.breakpoints.up("desktop"),
                    isDisabled: isAnnouncementArticle,
                    sizes: ["fluid"],
                  }),
                })}
                {!isSubscriber &&
                  provider.adslot({
                    adUnit: "d_lrec3b",
                    autoRefreshOptions: {
                      scrollBackRefreshOptions: { slotArticleEntityId: article.entityId },
                    },
                    breakpoint: theme.breakpoints.up("desktop"),
                    isDisabled: isAnnouncementArticle,
                    sizes: [
                      [300, 250],
                      [300, 600],
                    ],
                    stickyLabel: true,
                    stickySpacing: 40,
                    withLabel: true,
                    withLabelBackground: true,
                    withSticky: true,
                  })}
              </RightSectionContainer>
            )}

            {shouldRenderWidgets["beforeYouGoWidget"] && (
              <>
                {/** placeholder to make the position of topic widget to be evenly distributed */}
                <RightSectionPlaceholder />
                <RightSectionContainer $stickyStackedHeight={appBarStickyHeight}>
                  {provider.beforeYouGoWidget(provider, {
                    children: provider.scmpPollWidget({
                      adSlotProps: {
                        breakpoint: theme.breakpoints.up("desktop"),
                        zone,
                      },
                    }),
                  })}
                </RightSectionContainer>
              </>
            )}
          </>
        )}
      </StyledArticleContentRightSection>
      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "show",
            tabletUp: "show",
          },
          responsiveImageVariants: { desktopUp: "normal", mobileUp: "normal", tabletUp: "normal" },
          responsiveVariants: {
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["1020x680"],
            tabletUp: ImageSize["1020x680"],
          },
          toggleTitle: true,
          ...leadingProps,
        })}
      </Leading>
      <ContentContainer>
        <Author>
          {provider.authorNames({ withLocation: articleVariant !== "opinion" })}
          {isRecapArticleType && <SCMPLabLogo />}
        </Author>
        {articleVariant === "opinion" && (
          <AuthorShortBio>{provider.authorShortBio()}</AuthorShortBio>
        )}
        <PublishDateTime>{provider.publishedDate({ variant: "article-2.0" })}</PublishDateTime>

        <Correction>{provider.correction()}</Correction>
        <Content $isShowAdSlot={!isAnnouncementArticle}>
          {provider.body({
            extraComponentMap: {
              "inline-widget": null,
            },
            extraRenderFunctionMap: {
              "recirculation-widget": (_, keyIndex) => (
                <RecirculationWidgetInlineContainer key={keyIndex}>
                  {provider.recirculationWidget({
                    inlineAds: provider.adslot({
                      adUnit: "m_native2b",
                      breakpoint: theme.breakpoints.between("mobile", "desktop"),
                      isDisabled: isInlineAdSlotDisabled || isAnnouncementArticle,
                      sizes: ["fluid"],
                    }),
                    variant: "inline",
                  })}
                </RecirculationWidgetInlineContainer>
              ),
            },
            inlineAdSlots,
            preprocessSchema: (schema: Schema) => {
              const newSchema = [...schema];
              const targetAdSlotTagIndex = newSchema.findIndex(
                node => node.type === "inline-ad-slot" && node.attribs?.tag === "6",
              );
              const recirculationWidgetNodeIndex =
                targetAdSlotTagIndex === -1
                  ? newSchema.findLastIndex(node => node.type === "inline-ad-slot") + 1
                  : targetAdSlotTagIndex + 1;

              newSchema.splice(recirculationWidgetNodeIndex, 0, {
                type: "recirculation-widget",
              });

              return newSchema;
            },
          })}
        </Content>

        <PaywallContainer>{provider.paywall()}</PaywallContainer>

        {isShowInlinePaywall === true && (
          <PaywallAdContainer>
            {provider.adslot({
              adUnit: "m_banner3",
              autoRefreshOptions: {
                scrollBackRefreshOptions: {
                  slotArticleEntityId: article.entityId,
                },
              },
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isAnnouncementArticle || undefined,
              sizes: [
                [320, 100],
                [320, 50],
                [300, 100],
                [300, 50],
              ],
            })}
          </PaywallAdContainer>
        )}

        <AuthorDetail>
          {provider.authorDetails({
            variant: "generic",
          })}
        </AuthorDetail>

        <RelatedTopic>{provider.relatedTopics()}</RelatedTopic>

        <BottomCarousel>
          {provider.bottomCarouselWidget(provider, { shouldDisableAds: isAnnouncementArticle })}
        </BottomCarousel>

        <SeriesInline>{provider.series({ variant: "inline" })}</SeriesInline>

        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <SeriesCarousel>{provider.series({ variant: "carousel" })}</SeriesCarousel>
          </GlobalChecker>
        </ClientSideSuspense>
      </ContentContainer>
    </GenericArticleContainer>
  );
};

GenericArticle.displayName = "GenericArticle";
